/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, ICommerceApiSettings, ICreateActionContext, msdyn365Commerce } from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { Cart } from '@msdyn365-commerce/retail-proxy';
import { copyAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import Cookie from 'js-cookie';

import { buildCacheKey } from './utilities/utils';

/**
 * Input class for getCheckoutCart data action.
 */
export class GetCheckoutCartInput implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('ActiveCheckoutCart', this.apiSettings);

    public getCacheObjectType = () => 'ActiveCheckoutCart';

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext) => {
    return new GetCheckoutCartInput(inputData.requestContext.apiSettings);
};

const CartCookieName: string = msdyn365Commerce.CartCookie || 'cart';

/**
 * Data action to copy the cart for checkout actions.
 * @param input
 * @param ctx
 */
export async function getCheckoutCart(input: GetCheckoutCartInput, ctx: IActionContext): Promise<Cart> {
    const cartId = Cookie.get(CartCookieName) || '';
    if (cartId) {
        const currentCartState = await getCartState(ctx);
        const activeCart = currentCartState.cart;

        // Check if there is an active cart and it isn't empty
        if (activeCart && Object.keys(activeCart).length > 0) {
            const checkoutCart = await copyAsync({ callerContext: ctx }, activeCart.Id, 2);

            if (checkoutCart) {
                return Array.isArray(checkoutCart) ? checkoutCart[0] : checkoutCart;
            }
        } else {
            ctx.trace('[get-checkout-cart] Can\'t create a checkout cart since active cart is empty');
        }
    }

    return <Cart>{};
}

export const getCheckoutCartDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-checkout-cart',
    action: <IAction<Cart>>getCheckoutCart,
    input: createInput
});

export default getCheckoutCartDataAction;
